/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { sshKeyService, walletService, containerService } from '../../services/api';
import { useSelectedProject } from '../../hooks/useSelectedProject';
import { SSHKey, GeneratedSSHKey, SSHKeyType, WalletSummary, Container } from '../../types';
import { isKYCEnforcementEnabled } from '../../config/featureFlags';
import { Button, KV, Skeleton, StatusTag, Tag } from '../console/ui';
import SettingsDialog from './SettingsDialog';

const EMPTY_GENERATE = { name: '', key_type: 'ed25519' as SSHKeyType, key_size: 4096 };

const SSHKeysSection: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { selectedProjectId } = useSelectedProject();
  const projectArg = selectedProjectId || undefined;

  const [showAdd, setShowAdd] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [managing, setManaging] = useState<SSHKey | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [addForm, setAddForm] = useState({ name: '', public_key: '' });
  const [genForm, setGenForm] = useState(EMPTY_GENERATE);
  const [generatedKey, setGeneratedKey] = useState<GeneratedSSHKey | null>(null);
  const [privateCopied, setPrivateCopied] = useState(false);

  const { data: keysData, isLoading } = useQuery({
    queryKey: ['ssh-keys', selectedProjectId],
    queryFn: () => sshKeyService.list(projectArg).then((res) => res.data),
  });
  const keys: SSHKey[] = Array.isArray(keysData) ? keysData : keysData?.data || keysData?.keys || [];

  // Pods using a key block its deletion.
  const { data: containersData } = useQuery({
    queryKey: ['containers'],
    queryFn: () => containerService.getMyContainers().then((res) => res.data),
  });
  const containers: Container[] = useMemo(
    () => containersData?.containers || (Array.isArray(containersData) ? containersData : []),
    [containersData],
  );
  const podsUsingKey = (keyId: string) =>
    containers.filter((c) => {
      if (!c.ssh_keys) return false;
      try {
        return JSON.parse(c.ssh_keys).id === keyId;
      } catch {
        return false;
      }
    });

  const { data: walletData } = useQuery({
    queryKey: ['wallet'],
    queryFn: () => walletService.get().then((res) => res.data),
  });
  const wallet: WalletSummary | null = walletData?.data || walletData || null;
  const isPrepaid = wallet?.account_type === 'prepaid';
  const maxKeys = isPrepaid ? 1 : 20;
  const atPrepaidLimit = isPrepaid && keys.length >= 1;

  const limitError = (error: any) =>
    error.response?.status === 402 && error.response?.data?.error === 'ssh_key_limit_reached';

  const addMutation = useMutation({
    mutationFn: (data: { name: string; publicKey: string }) => sshKeyService.create(data.name, data.publicKey, projectArg),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ssh-keys'] });
      toast.success('SSH key added');
      setShowAdd(false);
      setAddForm({ name: '', public_key: '' });
    },
    onError: (error: any) => {
      if (limitError(error)) {
        setShowAdd(false);
        setShowUpgrade(true);
      } else {
        toast.error(error.response?.data?.message || error.response?.data?.error || 'Failed to add SSH key');
      }
    },
  });

  const generateMutation = useMutation({
    mutationFn: (data: { name: string; keyType: SSHKeyType; keySize?: number }) =>
      sshKeyService.generate(data.name, data.keyType, data.keySize, projectArg),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['ssh-keys'] });
      setGeneratedKey(response.data?.data || response.data);
      toast.success('SSH key generated');
    },
    onError: (error: any) => {
      if (limitError(error)) {
        setShowGenerate(false);
        setShowUpgrade(true);
      } else {
        toast.error(error.response?.data?.message || error.response?.data?.error || 'Failed to generate SSH key');
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => sshKeyService.delete(id, projectArg),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ssh-keys'] });
      toast.success('SSH key deleted');
      setConfirmDelete(false);
      setManaging(null);
    },
    onError: (error: any) => toast.error(error.response?.data?.error || 'Failed to delete SSH key'),
  });

  const setDefaultMutation = useMutation({
    mutationFn: (id: string) => sshKeyService.setDefault(id, projectArg),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ssh-keys'] });
      toast.success('Default SSH key updated');
      setManaging((k) => (k ? { ...k, is_default: true } : k));
    },
    onError: (error: any) => toast.error(error.response?.data?.error || 'Failed to set default key'),
  });

  const openAdd = () => (atPrepaidLimit ? setShowUpgrade(true) : setShowAdd(true));
  const openGenerate = () => (atPrepaidLimit ? setShowUpgrade(true) : setShowGenerate(true));

  const copy = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const copyPrivate = async () => {
    if (!generatedKey?.private_key) return;
    try {
      await navigator.clipboard.writeText(generatedKey.private_key);
      setPrivateCopied(true);
      toast.success('Private key copied to clipboard');
      setTimeout(() => setPrivateCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const downloadPrivate = () => {
    if (!generatedKey?.private_key) return;
    const url = URL.createObjectURL(new Blob([generatedKey.private_key], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${generatedKey.name.replace(/\s+/g, '_')}.pem`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Private key downloaded');
  };

  const closeGenerate = () => {
    setShowGenerate(false);
    setGeneratedKey(null);
    setPrivateCopied(false);
    setGenForm(EMPTY_GENERATE);
  };

  const closeManage = () => {
    setManaging(null);
    setConfirmDelete(false);
  };

  const keyType = (k: SSHKey) => `${(k.key_type || '').toUpperCase()}${k.key_type === 'rsa' && k.key_size ? ` ${k.key_size}` : ''}`;

  const blockingPods = managing
    ? podsUsingKey(managing.id).filter((c) => ['running', 'stopped'].includes(c.status?.toLowerCase() || ''))
    : [];

  return (
    <section className="ps-card p-5 flex flex-col gap-3.5" aria-labelledby="ssh-keys-heading">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h2 id="ssh-keys-heading" className="m-0 text-lg font-medium text-ps-text">
            SSH keys
          </h2>
          <p className="m-0 mt-1 text-ps-muted text-sm">Added to new GPU Pods. The default key is pre-selected at launch.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isPrepaid && (
            <span className="text-ps-subtle text-[13px]">
              {keys.length}/{maxKeys} key{maxKeys > 1 ? 's' : ''} used
            </span>
          )}
          <Button variant="secondary" onClick={openAdd}>
            Add key
          </Button>
          <Button variant="secondary" onClick={openGenerate}>
            Generate key
          </Button>
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-24" />
      ) : keys.length === 0 ? (
        <div className="p-5 text-center text-ps-muted text-sm border border-dashed border-ps-strong rounded-[10px]">
          No SSH keys yet. Add your public key or generate a new pair to connect to your pods.
        </div>
      ) : (
        <div className="overflow-x-auto border border-ps-border rounded-[10px]">
          <table className="ps-tbl">
            <thead>
              <tr>
                <th>Name</th>
                <th>Fingerprint</th>
                <th>Type</th>
                <th>Created</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {keys.map((key) => (
                <tr key={key.id}>
                  <td className="text-ps-text">
                    <span className="inline-block max-w-[180px] truncate align-bottom">{key.name}</span>
                    {key.is_default && (
                      <Tag tone="ok" className="ml-2">
                        Default
                      </Tag>
                    )}
                    {key.is_generated && (
                      <Tag tone="info" className="ml-2">
                        Generated
                      </Tag>
                    )}
                  </td>
                  <td className="font-mono text-xs text-ps-muted" title={key.fingerprint}>
                    {key.fingerprint?.length > 22 ? `${key.fingerprint.substring(0, 22)}…` : key.fingerprint}
                  </td>
                  <td>{keyType(key)}</td>
                  <td className="text-ps-muted">{format(new Date(key.created_at), 'd MMM yyyy')}</td>
                  <td className="text-right">
                    <Button variant="secondary" size="sm" onClick={() => setManaging(key)}>
                      Manage
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Manage key */}
      {managing && (
        <SettingsDialog title={confirmDelete ? 'Delete SSH key' : managing.name} onClose={closeManage}>
          {!confirmDelete ? (
            <>
              <div className="flex flex-col gap-2">
                <KV k="Type" v={keyType(managing)} />
                <KV k="Created" v={format(new Date(managing.created_at), 'd MMM yyyy')} />
                <KV k="Default" v={managing.is_default ? 'Yes' : 'No'} />
              </div>
              <div>
                <span className="ps-label">Fingerprint</span>
                <code className="block font-mono text-xs text-ps-muted break-all">{managing.fingerprint}</code>
              </div>
              <div>
                <label className="ps-label" htmlFor="ssh-pub">
                  Public key
                </label>
                <textarea id="ssh-pub" readOnly rows={3} value={managing.public_key} className="ps-input py-2 font-mono text-xs resize-none" />
              </div>
              <div className="flex flex-wrap gap-2.5">
                <Button variant="secondary" onClick={() => copy(managing.public_key, managing.id)}>
                  {copiedId === managing.id ? 'Copied' : 'Copy public key'}
                </Button>
                <Button
                  variant="secondary"
                  disabled={managing.is_default || setDefaultMutation.isPending}
                  onClick={() => setDefaultMutation.mutate(managing.id)}
                >
                  {managing.is_default ? 'Default key' : 'Set as default'}
                </Button>
                <Button variant="danger" className="sm:ml-auto" onClick={() => setConfirmDelete(true)}>
                  Delete
                </Button>
              </div>
            </>
          ) : blockingPods.length > 0 ? (
            <>
              <p className="m-0 px-3.5 py-3 rounded-[10px] bg-ps-warn-bg text-ps-warn text-sm">
                “{managing.name}” is in use by {blockingPods.length} pod{blockingPods.length > 1 ? 's' : ''}. Delete or terminate
                {blockingPods.length > 1 ? ' them' : ' it'} first.
              </p>
              <ul className="m-0 p-0 list-none flex flex-col gap-1.5 max-h-40 overflow-y-auto">
                {blockingPods.map((c) => (
                  <li key={c.id} className="flex justify-between items-center gap-2 px-3 py-2 rounded-md bg-ps-raised text-sm">
                    <span className="truncate text-ps-text">{c.name}</span>
                    <StatusTag status={c.status} />
                  </li>
                ))}
              </ul>
              <Button onClick={() => setConfirmDelete(false)}>Got it</Button>
            </>
          ) : (
            <>
              <p className="m-0 text-sm text-ps-muted">
                Delete <span className="text-ps-text font-semibold">“{managing.name}”</span>? This can’t be undone.
              </p>
              <div className="flex flex-wrap justify-end gap-2.5">
                <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
                  Cancel
                </Button>
                <Button variant="danger" onClick={() => deleteMutation.mutate(managing.id)} disabled={deleteMutation.isPending}>
                  {deleteMutation.isPending ? 'Deleting…' : 'Delete key'}
                </Button>
              </div>
            </>
          )}
        </SettingsDialog>
      )}

      {/* Add key */}
      {showAdd && (
        <SettingsDialog
          title="Add SSH key"
          onClose={() => {
            setShowAdd(false);
            setAddForm({ name: '', public_key: '' });
          }}
        >
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              addMutation.mutate({ name: addForm.name, publicKey: addForm.public_key });
            }}
          >
            <div>
              <label className="ps-label" htmlFor="ssh-add-name">
                Name
              </label>
              <input
                id="ssh-add-name"
                className="ps-input"
                value={addForm.name}
                onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                placeholder="e.g. my-laptop"
                required
              />
            </div>
            <div>
              <label className="ps-label" htmlFor="ssh-add-key">
                Public key
              </label>
              <textarea
                id="ssh-add-key"
                className="ps-input py-2 font-mono text-xs resize-none"
                rows={4}
                value={addForm.public_key}
                onChange={(e) => setAddForm({ ...addForm, public_key: e.target.value })}
                placeholder="ssh-ed25519 AAAA… or ssh-rsa AAAA…"
                required
              />
              <p className="m-0 mt-1.5 text-xs text-ps-subtle">Usually in ~/.ssh/id_ed25519.pub or ~/.ssh/id_rsa.pub</p>
            </div>
            <div className="flex flex-wrap justify-end gap-2.5">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowAdd(false);
                  setAddForm({ name: '', public_key: '' });
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={addMutation.isPending}>
                {addMutation.isPending ? 'Adding…' : 'Add key'}
              </Button>
            </div>
          </form>
        </SettingsDialog>
      )}

      {/* Generate key */}
      {showGenerate && (
        <SettingsDialog title={generatedKey ? 'Save your private key' : 'Generate SSH key'} onClose={closeGenerate}>
          {!generatedKey ? (
            <form
              className="flex flex-col gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                generateMutation.mutate({
                  name: genForm.name,
                  keyType: genForm.key_type,
                  keySize: genForm.key_type === 'rsa' ? genForm.key_size : undefined,
                });
              }}
            >
              <div>
                <label className="ps-label" htmlFor="ssh-gen-name">
                  Name
                </label>
                <input
                  id="ssh-gen-name"
                  className="ps-input"
                  value={genForm.name}
                  onChange={(e) => setGenForm({ ...genForm, name: e.target.value })}
                  placeholder="e.g. production"
                  required
                />
              </div>
              <div>
                <label className="ps-label" htmlFor="ssh-gen-type">
                  Key type
                </label>
                <select
                  id="ssh-gen-type"
                  className="ps-input"
                  value={genForm.key_type}
                  onChange={(e) => setGenForm({ ...genForm, key_type: e.target.value as SSHKeyType })}
                >
                  <option value="ed25519">ED25519 (recommended)</option>
                  <option value="rsa">RSA</option>
                </select>
                <p className="m-0 mt-1.5 text-xs text-ps-subtle">
                  ED25519 is faster and more secure. RSA works with older systems.
                </p>
              </div>
              {genForm.key_type === 'rsa' && (
                <div>
                  <label className="ps-label" htmlFor="ssh-gen-size">
                    Key size
                  </label>
                  <select
                    id="ssh-gen-size"
                    className="ps-input"
                    value={genForm.key_size}
                    onChange={(e) => setGenForm({ ...genForm, key_size: parseInt(e.target.value, 10) })}
                  >
                    <option value={2048}>2048 bits</option>
                    <option value={4096}>4096 bits (recommended)</option>
                  </select>
                </div>
              )}
              <div className="flex flex-wrap justify-end gap-2.5">
                <Button variant="secondary" onClick={closeGenerate}>
                  Cancel
                </Button>
                <Button type="submit" disabled={generateMutation.isPending}>
                  {generateMutation.isPending ? 'Generating…' : 'Generate key'}
                </Button>
              </div>
            </form>
          ) : (
            <>
              <p className="m-0 px-3.5 py-3 rounded-[10px] bg-ps-warn-bg text-ps-warn text-sm">
                This is the only time you can download this private key - it isn’t stored on our servers.
              </p>
              <div>
                <label className="ps-label" htmlFor="ssh-gen-priv">
                  Private key
                </label>
                <textarea
                  id="ssh-gen-priv"
                  readOnly
                  rows={8}
                  value={generatedKey.private_key}
                  className="ps-input py-2 font-mono text-xs resize-none"
                />
              </div>
              <div className="flex flex-wrap gap-2.5">
                <Button variant="secondary" className="flex-1" onClick={copyPrivate}>
                  {privateCopied ? 'Copied' : 'Copy to clipboard'}
                </Button>
                <Button className="flex-1" onClick={downloadPrivate}>
                  Download .pem
                </Button>
              </div>
              <Button variant="secondary" onClick={closeGenerate}>
                I’ve saved my private key
              </Button>
            </>
          )}
        </SettingsDialog>
      )}

      {/* Prepaid limit */}
      {showUpgrade && (
        <SettingsDialog title="SSH key limit reached" size="md" onClose={() => setShowUpgrade(false)}>
          <p className="m-0 text-sm text-ps-muted">
            Prepaid accounts can keep <span className="text-ps-text font-semibold">1 SSH key</span>. Complete KYC and move to a
            postpaid account to add more keys, pay at the end of the month and get higher resource limits.
          </p>
          <div className="flex flex-wrap justify-end gap-2.5">
            <Button variant="secondary" onClick={() => setShowUpgrade(false)}>
              Maybe later
            </Button>
            <Button
              onClick={() => {
                setShowUpgrade(false);
                navigate(isKYCEnforcementEnabled() ? '/kyc' : '/billing');
              }}
            >
              Complete KYC
            </Button>
          </div>
        </SettingsDialog>
      )}
    </section>
  );
};

export default SSHKeysSection;
